import { useNavigate } from "react-router-dom";
import { Camera, Search, Bookmark, ShoppingBag } from "lucide-react";

// Style Hub — two features only, per master spec (2026-02-26):
//   1. Outfit Rating (vibe-based, never analyses the person)
//   2. Find Similar Clothes (item identification + where-to-buy)
//
// Explicitly removed (v1 launch spec): appearance/face/hair/body/skin
// analysis CTAs, the legacy AI Stylist generator, the nearby-store map.
// The Style tab is now clothing-only. Map removed app-wide for launch.

const ONLINE_STORES = [
  { name: "Zara",              site: "https://www.zara.com" },
  { name: "H&M",               site: "https://www2.hm.com" },
  { name: "ASOS",              site: "https://www.asos.com" },
  { name: "Uniqlo",            site: "https://www.uniqlo.com" },
  { name: "SHEIN",             site: "https://www.shein.com" },
  { name: "Amazon",            site: "https://www.amazon.com" },
  { name: "Nike",              site: "https://www.nike.com" },
  { name: "Adidas",            site: "https://www.adidas.com" },
  { name: "StockX",            site: "https://stockx.com" },
  { name: "GOAT",              site: "https://www.goat.com" },
];

export default function StyleHubScreen() {
  const navigate = useNavigate();
  return (
    <div className="screen with-nav fade-in" data-testid="style-screen">
      <div className="top-bar">
        <div className="logo-mark">STYLE</div>
        <span className="premium-pill">FREE</span>
      </div>

      <div className="eyebrow" style={{ marginBottom: 8 }}>CLOTHING ONLY</div>
      <h2 className="h1" style={{ fontSize: 30, marginBottom: 14, lineHeight: 1.12 }}>
        Rate the fit.<br />
        <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>Find the pieces.</em>
      </h2>
      <p className="body" style={{ marginTop: 0, marginBottom: 22, color: "var(--text-dim)" }}>
        Two things only — see how well your outfit matches the vibe you&apos;re going for, or upload a piece you love and find where to buy it.
      </p>

      {/* Two-feature grid */}
      <div className="style-feature-grid" data-testid="style-features">
        <button
          type="button"
          className="style-feature-card"
          onClick={() => navigate("/rate")}
          data-testid="style-feature-rate"
        >
          <div className="style-feature-icon">
            <Camera size={22} strokeWidth={1.7} />
          </div>
          <div className="style-feature-title serif">Rate my outfit</div>
          <div className="style-feature-sub">Pick a vibe — Streetwear, Old Money, Date Night — and see how well your fit matches.</div>
        </button>

        <button
          type="button"
          className="style-feature-card"
          onClick={() => navigate("/find-similar")}
          data-testid="style-feature-find"
        >
          <div className="style-feature-icon">
            <Search size={22} strokeWidth={1.7} />
          </div>
          <div className="style-feature-title serif">Find similar clothes</div>
          <div className="style-feature-sub">Upload a piece you love — shoes, jacket, bag — get realistic places to buy.</div>
        </button>
      </div>

      <button
        type="button"
        className="btn btn-outline"
        style={{ marginTop: 12, justifyContent: "center", width: "100%" }}
        onClick={() => navigate("/saved")}
        data-testid="style-cta-saved"
      >
        <Bookmark size={14} /> My saved styles
      </button>

      {/* Where to buy — online */}
      <div className="outfit-section" data-testid="style-stores">
        <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
          <ShoppingBag size={12} strokeWidth={1.8} /> POPULAR ONLINE STORES
        </div>
        <div className="online-store-grid" data-testid="style-stores-online">
          {ONLINE_STORES.map((s) => (
            <a
              key={s.name}
              href={s.site}
              target="_blank"
              rel="noopener noreferrer"
              className="online-store-pill"
              data-testid={`style-store-online-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {s.name}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
